import React, { useState } from 'react';
import { Tabs, Tab, Box } from '@mui/material';

import { TP_COLORS } from '../../../../constants';
import { getSubjectColorNew, isMobileScreen } from '../../../../helper';

import { IGroupedTopic } from '../../../../store/SubTopicSlice';
import { ISubjectForProgress } from '../../../../store/ProgressSlice';

import AssessmentListItem from './AssessmentListItem';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface AssessmentTabProps {
  assessmentList: Array<IGroupedTopic>;
  subjectProgress: ISubjectForProgress | undefined;
}

const AssessmentTab: React.FC<AssessmentTabProps> = ({
  assessmentList,
  subjectProgress,
}) => {
  const isMobile = isMobileScreen();
  const [tabValue, setTabValue] = useState(0);

  const subjectColor = getSubjectColorNew(assessmentList[0].subject || '');

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getSubTopicProgress = (topicId: string, subTopicId: string) => {
    let isCompleted = false;

    if (subjectProgress) {
      const topicProgress = subjectProgress.topics.find(
        (item) => item._id === topicId
      );

      if (topicProgress && topicProgress.subTopics.length) {
        const subTopicProgress = topicProgress.subTopics.find(
          (item) => item._id === subTopicId
        );

        if (subTopicProgress && subTopicProgress.completion === 100) {
          isCompleted = true;
        }
      }
    }
    return isCompleted;
  };

  return (
    <Box component="section" sx={{ width: '100%' }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: subjectColor,
          borderRadius: '15px',
          marginBottom: '30px',
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleChange}
          aria-label="basic tabs example"
          variant="fullWidth"
          TabIndicatorProps={{
            sx: {
              backgroundColor: TP_COLORS.white,
              height: { xs: '87%', sm: '85%' },
              borderRadius: '11px',
              bottom: '4px',
            },
          }}
        >
          {assessmentList.map((topicItem, topicIndex) => {
            const isSelected = topicIndex === tabValue;

            const tabStyles: React.CSSProperties = {
              color: TP_COLORS.white,
              borderRadius: '7px',
              fontWeight: 600,
              zIndex: 1,
              margin: '6px',
            };

            if (isSelected) {
              tabStyles.color = TP_COLORS.gray.dark;
            }

            if (isMobile) {
              tabStyles.fontSize = '12px';
            }

            return (
              <Tab
                key={topicIndex}
                color="inherit"
                label={`${topicItem.name} (${topicItem.subTopics.length})`}
                style={tabStyles}
                {...a11yProps(topicIndex)}
              />
            );
          })}
        </Tabs>
      </Box>
      {assessmentList.map((topicItem, topicIndex) => (
        <CustomTabPanel key={topicIndex} value={tabValue} index={topicIndex}>
          {topicItem.subTopics.map((subTopicItem, subTopicIndex) => (
            <AssessmentListItem
              key={subTopicIndex}
              subTopic={subTopicItem}
              topicData={topicItem}
              assessmentProgress={getSubTopicProgress(
                topicItem._id,
                subTopicItem._id
              )}
            />
          ))}
        </CustomTabPanel>
      ))}
    </Box>
  );
};

export default AssessmentTab;
